/**
 * FeedbackImageGallery Component
 *
 * Displays feedback images as a thumbnail grid with a lightbox for full-size viewing.
 * Lightbox closes only via X button (no backdrop click), following project modal rules.
 */

import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { getFeedbackImageUrl } from '../../api/feedbackPortalApi';

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 8px;
  margin-top: 10px;
`;

const GalleryItem = styled.div`
  cursor: pointer;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const GalleryThumbnail = styled.img`
  width: 100%;
  height: 80px;
  object-fit: cover;
  display: block;
`;

const ImageLabel = styled.div`
  font-size: 11px;
  color: #6b7280;
  padding: 4px 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: #f9fafb;
`;

// Lightbox
const LightboxOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1002;
`;

const LightboxContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LightboxImage = styled.img`
  max-width: 90vw;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 4px;
`;

const LightboxCaption = styled.div`
  color: white;
  font-size: 14px;
  margin-top: 12px;
  text-align: center;
  max-width: 80vw;
  word-break: break-word;
`;

const LightboxCounter = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  margin-top: 4px;
`;

const LightboxClose = styled.button`
  position: fixed;
  top: 16px;
  right: 16px;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: white;
  font-size: 28px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1003;
  padding: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const NavButton = styled.button`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: white;
  font-size: 24px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1003;
  padding: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const PrevButton = styled(NavButton)`
  left: 16px;
`;

const NextButton = styled(NavButton)`
  right: 16px;
`;

const FeedbackImageGallery = ({ images = [] }) => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = (index) => {
    setLightboxIndex(index);
  };

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
  }, []);

  const goPrev = useCallback(() => {
    setLightboxIndex(prev => (prev > 0 ? prev - 1 : prev));
  }, []);

  const goNext = useCallback(() => {
    setLightboxIndex(prev => (prev < images.length - 1 ? prev + 1 : prev));
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    if (lightboxIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, closeLightbox, goPrev, goNext]);

  if (!images || images.length === 0) return null;

  return (
    <>
      <GalleryGrid>
        {images.map((img, index) => (
          <GalleryItem key={index} onClick={() => openLightbox(index)}>
            <GalleryThumbnail
              src={getFeedbackImageUrl(img.s3Key)}
              alt={img.name || `Image ${index + 1}`}
              loading="lazy"
            />
            {img.name && <ImageLabel title={img.name}>{img.name}</ImageLabel>}
          </GalleryItem>
        ))}
      </GalleryGrid>

      {lightboxIndex !== null && images[lightboxIndex] && (
        <LightboxOverlay>
          <LightboxClose onClick={closeLightbox}>&times;</LightboxClose>

          {images.length > 1 && (
            <>
              <PrevButton onClick={goPrev} disabled={lightboxIndex === 0}>
                &#8249;
              </PrevButton>
              <NextButton onClick={goNext} disabled={lightboxIndex === images.length - 1}>
                &#8250;
              </NextButton>
            </>
          )}

          <LightboxContent>
            <LightboxImage
              src={getFeedbackImageUrl(images[lightboxIndex].s3Key)}
              alt={images[lightboxIndex].name || `Image ${lightboxIndex + 1}`}
            />
            {images[lightboxIndex].name && (
              <LightboxCaption>{images[lightboxIndex].name}</LightboxCaption>
            )}
            {images.length > 1 && (
              <LightboxCounter>{lightboxIndex + 1} / {images.length}</LightboxCounter>
            )}
          </LightboxContent>
        </LightboxOverlay>
      )}
    </>
  );
};

export default FeedbackImageGallery;
